import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { FaFileInvoiceDollar, FaSave, FaTimes } from "react-icons/fa";

const PurchaseBillForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [banks, setBanks] = useState([]);

  const [formData, setFormData] = useState({
    supplier_id: "",
    bill_number: "",
    payment_method: "Cash",
    bank_id: "",
    cheque_number: "",
    cheque_date: "",
    amount: "",
    tax_percentage: "0",
    tax_amount: "0",
    description: "",
    bill_date: new Date().toISOString().split('T')[0],
    due_date: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [suppRes, bankRes] = await Promise.all([
          api.get("suppliers/"),
          api.get("banks/"),
        ]);
        setSuppliers(suppRes.data);
        setBanks(bankRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      
      // Auto calculate tax
      if (name === "amount" || name === "tax_percentage") {
        const amount = parseFloat(name === "amount" ? value : prev.amount) || 0;
        const taxPercent = parseFloat(name === "tax_percentage" ? value : prev.tax_percentage) || 0;
        updated.tax_amount = (amount * (taxPercent / 100)).toFixed(2);
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.supplier_id) {
      alert("Amount and Supplier are required");
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        amount: Number(formData.amount),
        tax_percentage: Number(formData.tax_percentage),
        tax_amount: Number(formData.tax_amount),
        supplier_id: parseInt(formData.supplier_id),
        bank_id: formData.bank_id ? parseInt(formData.bank_id) : null,
        bill_date: formData.bill_date ? new Date(formData.bill_date).toISOString() : null,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString().split('T')[0] : null,
        cheque_date: formData.cheque_date ? new Date(formData.cheque_date).toISOString().split('T')[0] : null,
      };
      
      await api.post("purchase_bills/", payload);
      alert("Purchase Bill saved successfully!");
      navigate("/dashboard/transactions/purchase-bills/list");
    } catch (err) {
      console.error("Error saving purchase bill:", err);
      alert("Failed to save purchase bill. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4 max-w-2xl mx-auto">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-primary text-white d-flex align-items-center gap-2">
          <FaFileInvoiceDollar />
          <h4 className="mb-0">Add New Purchase Bill</h4>
        </div>
        <div className="card-body p-4">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Bill Date *</label>
                <input
                  type="date"
                  name="bill_date"
                  className="form-control"
                  value={formData.bill_date}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Due Date</label>
                <input
                  type="date"
                  name="due_date"
                  className="form-control"
                  value={formData.due_date}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Bill Number (Optional)</label>
                <input
                  type="text"
                  name="bill_number"
                  className="form-control"
                  value={formData.bill_number}
                  onChange={handleChange}
                  placeholder="Auto-generated if left blank"
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Select Supplier *</label>
                <select
                  name="supplier_id"
                  className="form-select"
                  value={formData.supplier_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select Supplier --</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.supplier_name || s.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Payment Method</label>
              <select
                name="payment_method"
                className="form-select"
                value={formData.payment_method}
                onChange={handleChange}
              >
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cheque">Cheque</option>
                <option value="Credit Card">Credit Card</option>
              </select>
            </div>

            {(formData.payment_method === "Bank Transfer" || formData.payment_method === "Credit Card") && (
              <div className="mb-3">
                <label className="form-label fw-bold">Select Bank</label>
                <select
                  name="bank_id"
                  className="form-select"
                  value={formData.bank_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select Bank --</option>
                  {banks.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            )}

            {formData.payment_method === "Cheque" && (
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Cheque Number</label>
                  <input
                    type="text"
                    name="cheque_number"
                    className="form-control"
                    value={formData.cheque_number}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Cheque Date</label>
                  <input
                    type="date"
                    name="cheque_date"
                    className="form-control"
                    value={formData.cheque_date}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold text-danger">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="amount"
                  className="form-control"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  placeholder="0.00"
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label fw-bold">Tax (%)</label>
                <input
                  type="number"
                  step="0.01"
                  name="tax_percentage"
                  className="form-control"
                  value={formData.tax_percentage}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label fw-bold">Tax Amount</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.tax_amount}
                  readOnly
                  disabled
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label fw-bold">Product / Service Details *</label>
              <textarea
                name="description"
                className="form-control"
                rows="3"
                value={formData.description}
                onChange={handleChange}
                placeholder="Details of product or service purchased..."
                required
              ></textarea>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary d-flex align-items-center gap-2"
                onClick={() => navigate("/dashboard/transactions/purchase-bills/list")}
              >
                <FaTimes /> Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary d-flex align-items-center gap-2"
                disabled={loading}
              >
                <FaSave /> {loading ? "Saving..." : "Save Purchase Bill"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PurchaseBillForm;
